import React, { useState, useEffect } from 'react';
import adminService, { CreatedDevice } from '../services/adminService';
import '../styles/Admin.css';

const Admin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [createdDevices, setCreatedDevices] = useState<CreatedDevice[]>([]);
  const [error, setError] = useState('');
  const [deviceType, setDeviceType] = useState<'Single Color' | 'RGB'>('RGB');
  const [initialLoading, setInitialLoading] = useState(true);
  const [lockedChannelID, setLockedChannelID] = useState('');
  const [lockedPlatform, setLockedPlatform] = useState<'twitch' | 'youtube' | 'kick'>('twitch');

  useEffect(() => {
    const loadDevices = async () => {
      try {
        const devices = await adminService.getAllDevices();
        setCreatedDevices(devices);
      } catch (err: any) {
        console.error('Failed to load devices:', err);
        setError('Failed to load existing devices');
      } finally {
        setInitialLoading(false);
      }
    };

    loadDevices();
  }, []);

  const handleAddDevice = async () => {
    setLoading(true);
    setError('');
    try {
      const payload: any = { device_type: deviceType };
      
      // Add locked stream if channelID is provided
      if (lockedChannelID.trim()) {
        payload.locked_stream = {
          channelID: lockedChannelID.trim(),
          platform: lockedPlatform,
        };
      }
      
      const device = await adminService.createDevice(payload);
      setCreatedDevices([device, ...createdDevices]);
      
      // Reset locked stream fields
      setLockedChannelID('');
      setLockedPlatform('twitch');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create device';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <h1>Device Administration</h1>
        
        <div className="admin-controls">
          <div className="device-type-selector">
            <label htmlFor="device-type">Device Type:</label>
            <select 
              id="device-type"
              value={deviceType} 
              onChange={(e) => setDeviceType(e.target.value as 'Single Color' | 'RGB')}
              className="device-type-select"
              disabled={loading}
            >
              <option value="RGB">RGB</option>
              <option value="Single Color">Single Color</option>
            </select>
          </div>
          
          <div style={{ marginTop: 20, padding: '15px', backgroundColor: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: 14, color: 'var(--text-primary)' }}>
              Optional: Pre-Built Locked Stream
            </h3>
            <p style={{ margin: '0 0 15px 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Add a locked stream that cannot be deleted or modified by users
            </p>
            
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="locked-platform" style={{ display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 600 }}>
                  Platform
                </label>
                <select
                  id="locked-platform"
                  value={lockedPlatform}
                  onChange={(e) => setLockedPlatform(e.target.value as 'twitch' | 'youtube' | 'kick')}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: 4,
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="twitch">Twitch</option>
                  <option value="youtube">YouTube</option>
                  <option value="kick">Kick</option>
                </select>
              </div>
              
              <div style={{ flex: 2 }}>
                <label htmlFor="locked-channel" style={{ display: 'block', marginBottom: 5, fontSize: 12, fontWeight: 600 }}>
                  Channel ID
                </label>
                <input
                  id="locked-channel"
                  type="text"
                  value={lockedChannelID}
                  onChange={(e) => setLockedChannelID(e.target.value)}
                  placeholder="Enter channel ID (optional)"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: 4,
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>
          </div>
          
          <button 
            onClick={handleAddDevice} 
            disabled={loading}
            className="btn-add-device"
            style={{ marginTop: 20 }}
          >
            {loading ? 'Creating...' : 'Add New Device'}
          </button>
          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="devices-list">
          {initialLoading ? (
            <p className="loading">Loading devices...</p>
          ) : createdDevices.length === 0 ? (
            <p className="no-devices">No devices created yet. Click "Add New Device" to create your first device.</p>
          ) : (
            createdDevices.map((device) => (
              <div key={device.id} className="device-card">
                <div className="device-info">
                  <div className="device-hash">
                    <strong>Hash:</strong>
                    <code>{device.device_hash}</code>
                    <button 
                      className="btn-copy"
                      onClick={() => copyToClipboard(device.device_hash)}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                  <div className="device-meta">
                    <span className="device-type-badge">
                      {device.device_type || 'RGB'}
                    </span>
                    <span className="created-at">
                      Created: {new Date(device.created_at).toLocaleString()}
                    </span>
                    <div className="assigned-email" style={{ marginTop: 6 }}>
                      <strong>Assigned to:</strong>{' '}
                      {device.assigned_email ? device.assigned_email : 'Unassigned'}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
